// src/scenes/companies/CompanyProducts.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  CircularProgress,
  IconButton,
  Avatar,
  useTheme,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Add, Edit, Delete } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import axiosInstance from "../../axiosInstance";
import { tokens } from "../../theme";
import { toast } from "react-toastify";
import Header from "../../components/Header";

const initialForm = { title: "", mrp: "", dp: "", productImg: null };

const CompanyProducts = () => {
  const { id: companyId } = useParams();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  // Dialog states
  const [addEditOpen, setAddEditOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(initialForm);
  const [deleteId, setDeleteId] = useState(null);

  /** ------------ FETCH ------------ */
  const fetchProducts = useCallback(() => {
    setLoading(true);
    axiosInstance
      .get(`/companies/${companyId}/products`)
      .then((res) => setProducts(res.data || []))
      .catch(() => toast.error("Failed to load products"))
      .finally(() => setLoading(false));
  }, [companyId]); // Add companyId as dependency

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]); // Now includes fetchProducts in dependencies

  /** ------------ ADD / EDIT ------------ */
  const openAddEdit = (row = null) => {
    setEditing(row);
    setFormData(
      row
        ? { title: row.title, mrp: row.mrp, dp: row.dp, productImg: null }
        : initialForm,
    );
    setAddEditOpen(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleFileChange = (file) => {
    setFormData((p) => ({ ...p, productImg: file }));
  };

  const handleConfirmSave = () => {
    if (!formData.title.trim()) return toast.warn("Product title is required");
    setConfirmOpen(true);
  };

  const submitAddEdit = () => {
    setLoading(true);
    const form = new FormData();
    form.append("title", formData.title);
    form.append("mrp", formData.mrp);
    form.append("dp", formData.dp);
    if (formData.productImg) form.append("productImg", formData.productImg);

    const request = editing
      ? axiosInstance.put(
          `/companies/${companyId}/products/${editing.id}`,
          form,
          { headers: { "Content-Type": "multipart/form-data" } },
        )
      : axiosInstance.post(`/companies/${companyId}/products`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });

    request
      .then(() => {
        toast.success(`Product ${editing ? "updated" : "added"} successfully`);
        fetchProducts();
        setAddEditOpen(false);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          `Failed to ${editing ? "update" : "add"} product`;
        toast.error(message);
      })

      .finally(() => {
        setLoading(false);
        setConfirmOpen(false);
      });
  };

  /** ------------ DELETE ------------ */
  const openDelete = (id) => {
    setDeleteId(id);
    setDeleteOpen(true);
  };

  const submitDelete = () => {
    setLoading(true);
    axiosInstance
      .delete(`/companies/${companyId}/products/${deleteId}`)
      .then(() => {
        toast.success("Product deleted");
        fetchProducts();
      })
      .catch(() => toast.error("Failed to delete product"))
      .finally(() => {
        setLoading(false);
        setDeleteOpen(false);
        setDeleteId(null);
      });
  };

  /** ------------ GRID ------------ */
  const columns = useMemo(
    () => [
      {
        field: "imagePath",
        headerName: "Image",
        width: 100,
        renderCell: (params) => (
          <Avatar
            src={params.value}
            alt={params.row.title}
            variant="rounded"
            sx={{ width: 60, height: 60 }}
          />
        ),
      },
      { field: "title", headerName: "Title", flex: 1, minWidth: 150 },
      { field: "mrp", headerName: "MRP", width: 100 },
      { field: "dp", headerName: "DP", width: 100 },
      {
        field: "actions",
        headerName: "Actions",
        width: 140,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <Box display="flex" gap={1}>
            <IconButton
              color="primary"
              disabled={loading}
              onClick={() => openAddEdit(row)}
            >
              <Edit />
            </IconButton>
            <IconButton
              color="error"
              disabled={loading}
              onClick={() => openDelete(row.id)}
            >
              <Delete />
            </IconButton>
          </Box>
        ),
      },
    ],
    [loading],
  );

  return (
    <Box m="20px">
      <Header title="COMPANY PRODUCTS" subtitle={`Company ID: ${companyId}`} />

      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button
          startIcon={loading ? <CircularProgress size={16} /> : <Add />}
          variant="contained"
          sx={{
            backgroundColor: colors.greenAccent[500],
            color: colors.grey[900],
          }}
          onClick={() => openAddEdit()}
          disabled={loading}
        >
          Add Product
        </Button>
      </Box>

      <Box
        height="70vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
        }}
      >
        <DataGrid
          rows={products}
          columns={columns}
          getRowId={(row) => row.id}
          disableRowSelectionOnClick
          pageSizeOptions={[5, 10, 20]}
          loading={loading}
          initialState={{
            pagination: { paginationModel: { pageSize: 10, page: 0 } },
          }}
        />
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog
        open={addEditOpen}
        onClose={() => setAddEditOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{editing ? "Edit Product" : "Add Product"}</DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, mt: 1 }}>
          <TextField
            label="Title"
            name="title"
            fullWidth
            value={formData.title}
            onChange={handleFormChange}
            autoFocus
          />
          <TextField
            label="MRP"
            name="mrp"
            fullWidth
            type="number"
            value={formData.mrp}
            onChange={handleFormChange}
          />
          <TextField
            label="DP"
            name="dp"
            fullWidth
            type="number"
            value={formData.dp}
            onChange={handleFormChange}
          />
          <Box>
            <Typography variant="subtitle1">Image</Typography>
            <Box display="flex" gap={2} alignItems="center" mt={1}>
              {editing?.imagePath && !formData.productImg && (
                <Avatar
                  src={editing.imagePath}
                  sx={{ width: 80, height: 80 }}
                  variant="rounded"
                />
              )}
              {formData.productImg && (
                <Avatar
                  src={URL.createObjectURL(formData.productImg)}
                  sx={{ width: 80, height: 80 }}
                  variant="rounded"
                />
              )}
              <Button variant="outlined" component="label">
                Upload
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => handleFileChange(e.target.files[0])}
                />
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAddEditOpen(false)}
            disabled={loading}
            color="error"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSave}
            disabled={loading}
            sx={{
              backgroundColor: colors.greenAccent[500],
              color: colors.grey[900],
            }}
          >
            {loading ? <CircularProgress size={18} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Confirm */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Save this product?</DialogTitle>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={submitAddEdit}
            disabled={loading}
            sx={{
              backgroundColor: colors.greenAccent[500],
              color: colors.grey[900],
            }}
          >
            {loading ? <CircularProgress size={18} /> : "Yes, Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete this product?</DialogTitle>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={submitDelete}
            disabled={loading}
            sx={{ color: colors.redAccent[500] }}
          >
            {loading ? <CircularProgress size={18} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompanyProducts;